"use client"

import { useEffect, type RefObject } from "react"

type RevealTarget = {
  readonly selector: string
  readonly x?: number
  readonly y?: number
  readonly scale?: number
  readonly stagger?: number
}

export function useAtlasReveal(
  scopeRef: RefObject<HTMLElement | HTMLDivElement | null>,
  targets: readonly RevealTarget[],
) {
  useEffect(() => {
    const scope = scopeRef.current
    if (!scope) return

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    const elements = targets.flatMap((target) =>
      Array.from(scope.querySelectorAll<HTMLElement>(target.selector)).map(
        (element, index) => ({ element, index, target }),
      ),
    )

    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      elements.forEach(({ element }) => element.classList.add("atlas-reveal-visible"))
      return
    }

    elements.forEach(({ element, index, target }) => {
      element.classList.add("atlas-reveal")
      element.style.setProperty("--atlas-reveal-x", `${target.x ?? 0}px`)
      element.style.setProperty("--atlas-reveal-y", `${target.y ?? 32}px`)
      element.style.setProperty("--atlas-reveal-scale", `${target.scale ?? 1}`)
      element.style.setProperty(
        "--atlas-reveal-delay",
        `${(target.stagger ?? 0) * index}ms`,
      )
    })

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add("atlas-reveal-visible")
          observer.unobserve(entry.target)
        })
      },
      { rootMargin: "0px 0px -12%", threshold: 0.08 },
    )

    elements.forEach(({ element }) => observer.observe(element))
    return () => observer.disconnect()
  }, [scopeRef, targets])
}
